import { useState } from "react";
import { Box, Button, Chip, List, ListItemButton, ListItemText, Paper, TextField, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { Add as AddIcon, WarningAmber as ReviewIcon } from "@mui/icons-material";
import type { ArchivedProvision, Provision, Severity } from "../../services/lensService";
import { SEVERITIES, formatWhen, isValidKey, severityColour, toKey } from "./lensUtils";


const useStyles = makeStyles()((theme) => ({
    root: { padding: theme.spacing(4) },
    header: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: theme.spacing(2) },
    addForm: { display: "flex", gap: theme.spacing(2), alignItems: "flex-start", marginBottom: theme.spacing(3) },
    pills: { display: "flex", gap: theme.spacing(1.5), flexWrap: "wrap", marginBottom: theme.spacing(2) },
    scroll: { maxHeight: 520, overflowY: "auto" },
    dot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0, marginRight: theme.spacing(2) },
    empty: { padding: theme.spacing(6), textAlign: "center", color: "#999" },
    archived: { marginTop: theme.spacing(4), borderTop: "1px solid #eee", paddingTop: theme.spacing(2) },
    archivedRow: { display: "flex", alignItems: "center", gap: theme.spacing(2), padding: theme.spacing(1, 0) },
}));

interface ProvisionListProps {
    provisions: Provision[];
    archived: ArchivedProvision[];
    selectedKey: string | null;
    readOnly: boolean;
    isBusy: boolean;
    onSelect: (key: string) => void;
    onCreate: (key: string, name: string) => void;
    onRestore: (key: string) => void;
}

const ProvisionList = ({ provisions, archived, selectedKey, readOnly, isBusy, onSelect, onCreate, onRestore }: ProvisionListProps) => {
    const { classes, theme } = useStyles();
    const [query, setQuery] = useState("");
    const [severityFilter, setSeverityFilter] = useState<Severity | null>(null);
    const [needsReview, setNeedsReview] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [newName, setNewName] = useState("");
    const [showArchived, setShowArchived] = useState(false);

    const newKey = toKey(newName);
    const liveClash = newKey ? provisions.find((p) => p.key === newKey) : undefined;
    const archivedClash = newKey ? archived.find((p) => p.key === newKey) : undefined;
    const clashMessage = liveClash
        ? `This playbook already has "${liveClash.name}". Select it in the list to edit it.`
        : archivedClash
          ? `"${archivedClash.name}" was archived ${formatWhen(archivedClash.archivedOn)}. Restore it instead.`
          : undefined;

    const needle = query.trim().toLowerCase();
    const visible = provisions.filter(
        (p) =>
            (!severityFilter || p.position.absenceSeverity === severityFilter) &&
            (!needsReview || !!p.reviewNote) &&
            (!needle || p.name.toLowerCase().includes(needle) || p.key.includes(needle)),
    );
    const draftCount = provisions.filter((p) => p.position.status === "draft").length;
    const reviewCount = provisions.filter((p) => p.reviewNote).length;

    return (
        <Paper className={classes.root}>
            <Box className={classes.header}>
                <Typography variant="h6">Provisions</Typography>
                {!readOnly && (
                    <Button size="small" startIcon={<AddIcon />} onClick={() => setAddOpen((o) => !o)}>
                        Add
                    </Button>
                )}
            </Box>

            {addOpen && !readOnly && (
                <Box className={classes.addForm}>
                    <TextField
                        size="small"
                        label="Provision name"
                        placeholder="e.g. Holiday entitlement"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        error={!!liveClash}
                        helperText={clashMessage ?? (newKey && `Key: ${newKey}`)}
                        fullWidth
                    />
                    {archivedClash ? (
                        <Button
                            variant="contained"
                            disabled={isBusy}
                            onClick={() => {
                                onRestore(archivedClash.key);
                                setNewName("");
                                setAddOpen(false);
                            }}
                        >
                            Restore
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            disabled={isBusy || !isValidKey(newKey) || !!liveClash}
                            onClick={() => {
                                onCreate(newKey, newName.trim());
                                setNewName("");
                                setAddOpen(false);
                            }}
                        >
                            Add
                        </Button>
                    )}
                </Box>
            )}

            <Box className={classes.pills}>
                {SEVERITIES.map((severity) => {
                    const on = severityFilter === severity;
                    return (
                        <Chip
                            key={severity}
                            size="small"
                            color={severityColour[severity]}
                            variant={on ? "filled" : "outlined"}
                            label={`${provisions.filter((p) => p.position.absenceSeverity === severity).length} ${severity}`}
                            onClick={() => setSeverityFilter(on ? null : severity)}
                        />
                    );
                })}
                {draftCount > 0 && <Chip size="small" variant="outlined" label={`${draftCount} draft`} />}
                {(reviewCount > 0 || needsReview) && (
                    <Chip
                        size="small"
                        color="warning"
                        icon={<ReviewIcon />}
                        variant={needsReview ? "filled" : "outlined"}
                        label={`Needs review (${reviewCount})`}
                        onClick={() => setNeedsReview((on) => !on)}
                    />
                )}
            </Box>

            <TextField
                size="small"
                placeholder="Filter provisions…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                fullWidth
            />

            <Box className={classes.scroll}>
                {visible.length === 0 ? (
                    <Typography variant="body2" className={classes.empty}>
                        {provisions.length === 0
                            ? "No provisions yet. Add one, or draft a set from a precedent."
                            : "No provisions match."}
                    </Typography>
                ) : (
                    <List dense>
                        {visible.map((provision) => (
                            <ListItemButton
                                key={provision.key}
                                selected={provision.key === selectedKey}
                                onClick={() => onSelect(provision.key)}
                            >
                                <span
                                    className={classes.dot}
                                    style={{ backgroundColor: theme.palette[severityColour[provision.position.absenceSeverity]].main }}
                                />
                                <ListItemText primary={provision.name || provision.key} />
                                {provision.reviewNote && (
                                    <ReviewIcon
                                        fontSize="small"
                                        color="warning"
                                        titleAccess="Needs review"
                                        style={{ marginRight: theme.spacing(1) }}
                                    />
                                )}
                                <Typography
                                    variant="caption"
                                    style={{
                                        fontWeight: 600,
                                        color: provision.position.status === "draft" ? "#7a5cf0" : theme.palette.success.main,
                                    }}
                                >
                                    {provision.position.status === "draft" ? "draft" : "approved"}
                                </Typography>
                            </ListItemButton>
                        ))}
                    </List>
                )}
            </Box>

            {archived.length > 0 && (
                <Box className={classes.archived}>
                    <Button size="small" onClick={() => setShowArchived((s) => !s)}>
                        {showArchived ? "Hide" : "Show"} archived ({archived.length})
                    </Button>
                    {showArchived &&
                        archived.map((provision) => (
                            <Box key={provision.key} className={classes.archivedRow}>
                                <Typography variant="body2" style={{ flexGrow: 1, color: "#777" }}>
                                    {provision.name} · archived {formatWhen(provision.archivedOn)}
                                </Typography>
                                {!readOnly && (
                                    <Button size="small" disabled={isBusy} onClick={() => onRestore(provision.key)}>
                                        Restore
                                    </Button>
                                )}
                            </Box>
                        ))}
                </Box>
            )}
        </Paper>
    );
};

export default ProvisionList;
